import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
// MUI
import { Alert, AlertTitle, Box, Link, Paper, Typography } from "@mui/material";
// Say
import {
  setAuthToken,
  LogIn as SayLogIn,
  isLoggedIn,
  REFRESH_TOKEN,
} from "@asayinc/component-library";
// redux/data
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectPrevPage } from "../../store/commonReducer";
import { useAttemptLoginMutation } from "../../store/ssoUser";
import {
  initialState as initialAppConfig,
  useGetAppConfigQuery,
} from "../../store/appConfig";
// analytics
import { identify, track } from "../../analytics";
import { rootApi } from "../../store/rootApi";
import { useTrackEventMutation } from "../../store/consumerUser";
import { UiEventActions, UiEventTypes } from "../../store/consumerUser/types";

interface FormFields {
  email: string;
  password: string;
}

/**
 * Maps RTK Query status to string values used in Say Login Component
 */
const loginStatusMap = {
  uninitialized: "idle",
  pending: "loading",
  rejected: "rejected",
};

const Login = (): JSX.Element => {
  const [trackEvent] = useTrackEventMutation();
  const isUserLoggedIn = !!isLoggedIn();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const prevPage = useAppSelector(selectPrevPage);
  const [login, loginResult] = useAttemptLoginMutation();
  const loginStatus = loginStatusMap[loginResult.status];
  const {
    data: { maintenanceMode, maintenanceModeMigrate } = initialAppConfig,
    isError,
  } = useGetAppConfigQuery();

  const isMaintenanceMode = isError
    ? process.env.REACT_APP_MAINTENANCE_MODE === "true"
    : maintenanceMode;

  const redirectUrl = prevPage || "/";

  useEffect(() => {
    if (loginResult.isSuccess) {
      const { user, token, refreshToken } = loginResult.data;
      window.localStorage.setItem(REFRESH_TOKEN, refreshToken);
      setAuthToken({ value: token });
      identify({
        id: user.pk,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
      });
      trackEvent({
        action: UiEventActions.Login,
        type: UiEventTypes.Click,
        sourceUrl: location.href,
        targetUrl: null,
      });
      track({ name: "Login Attempted", success: true });
    } else if (loginResult.isError) {
      track({ name: "Login Attempted", success: false });
    }
  }, [loginResult]);

  useEffect(() => {
    document.title = "Sign In | Say Issuer Portal";
    dispatch(rootApi.util.resetApiState());
  }, []);

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/forgot-password");
  };

  const attemptLogin = (data: FormFields) => {
    login(data);
  };

  useEffect(() => {
    if (!maintenanceModeMigrate) return;
    const timer = setTimeout(() => {
      window.location.href = "https://issuerportal.saytechnologies.com";
    }, 5000);
    return () => clearTimeout(timer);
  }, [maintenanceModeMigrate]);

  if (maintenanceModeMigrate) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        px={2}
      >
        <Paper
          elevation={3}
          sx={{ maxWidth: 560, width: "100%", p: 6, borderRadius: 3 }}
        >
          <Alert severity="info" sx={{ mb: 4 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              Issuer Portal Has Moved!
            </AlertTitle>
          </Alert>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            The new domain is at{" "}
            <Link
              href="https://issuerportal.saytechnologies.com"
              underline="hover"
              fontWeight={600}
            >
              https://issuerportal.saytechnologies.com
            </Link>
            . Please update your bookmarks.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Credentials remain the same.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, fontStyle: "italic" }}
          >
            You will be redirected automatically in 5 seconds.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isMaintenanceMode) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        px={2}
      >
        <Paper
          elevation={3}
          sx={{ maxWidth: 520, width: "100%", p: 5, borderRadius: 2 }}
        >
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              Issuer Portal Upgrade
            </AlertTitle>
          </Alert>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            Issuer Portal is in the process of being upgraded to a new version.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            For questions, email:{" "}
            <Link href="mailto:say-group@robinhood.com" underline="hover">
              say-group@robinhood.com
            </Link>
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isUserLoggedIn || loginResult.isSuccess) {
    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <SayLogIn
      login={attemptLogin}
      loginState={loginStatus}
      title="Sign in to Issuer Portal"
      goToForgotPassword={handleForgotPassword}
      bgColor="none"
    />
  );
};

export default Login;
